import {useEffect, useMemo, useState} from 'react';


export type Subscriber<DataT extends any[]> = (...data: DataT) => void;
export type SideChannel<DataT extends any[]> = {
  getLastPublication: () => DataT,
  subscribe: (fn: Subscriber<DataT>) => void,
  unsubscribe: (fn: Subscriber<DataT>) => void,
  publish: (...data: DataT) => void,
};


export function useSideChannel<DataT extends any[]>(...initialPublication: DataT): SideChannel<DataT>{
  const channel = useMemo(() => {
    const subscribers: Subscriber<DataT>[] = [];
    let lastPublication = initialPublication;

    function unsubscribe(fn: Subscriber<DataT>){
      const idx = subscribers.indexOf(fn);

      if (idx > -1){
        subscribers.splice(idx, 1);
      }
    }

    function subscribe(fn: Subscriber<DataT>){
      subscribers.push(fn);
      fn(...(lastPublication || []));

      return () => unsubscribe(fn);
    }

    function publish(...data: DataT){
      lastPublication = data;
      subscribers.forEach(fn => fn(...data));
    }

    return ({
      getLastPublication(){return lastPublication;},
      subscribe,
      unsubscribe,
      publish,
    });
  }, []);

  return channel;
}

export function useSideChannelSubscription<DataT extends any[]>(sidechannel: SideChannel<DataT>, indices: number): DataT[number];
export function useSideChannelSubscription<DataT extends any[]>(sidechannel: SideChannel<DataT>, indices: number[]): DataT[number][];
export function useSideChannelSubscription<DataT extends any[]>(sidechannel: SideChannel<DataT>, indices: number | number[] = 0): DataT[number] | DataT[number][]{
  const [data, setData] = useState<DataT | undefined>(undefined);
  indices = Array.isArray(indices) ? indices : [indices];

  useEffect(() => sidechannel?.subscribe((...data: DataT) => {
    data = indices.map(i => data[i]) as DataT;

    if (data.length === 1){
      data = data[0];
    }

    setData(data);
  }), [sidechannel, indices.join(',')]);

  return data;
}
